/**
 * Best-effort client-side check for whether a voice session could actually
 * start — i.e. whether an xAI API key is connected somewhere the backend's
 * credential tier walk (user → space → org) would find it.
 *
 * There is no backend pre-flight endpoint (ws-presign carries no voice info),
 * so this is inference over the credential listing hooks — and the org tier
 * is only visible to org admins (`useOrgCredentials` is admin-gated). Hence
 * three outcomes:
 *
 *  - `available`   — an xAI key is connected in a visible tier.
 *  - `unavailable` — DEFINITIVE: the caller is an admin, every tier is
 *                    loaded, and no xAI key exists anywhere.
 *  - `unknown`     — a non-admin with no personal/space key: the org tier is
 *                    invisible to them. Treat as optimistically available —
 *                    NEVER hide or disable the mic on `unknown`, or only
 *                    admins would ever see voice work. The `voice_start`
 *                    error frame (`VOICE_CREDENTIAL_MISSING`) is the
 *                    authority.
 *
 * Live recovery is free: the identity hooks invalidate on `optr.identity.*`
 * platform events, so connecting a key flips this to `available` untouched.
 */

import {
  useMyCredentials,
  useOrgCredentials,
  useSpaceCredentials,
} from '../hooks/useIdentityCredentials';
import { useAuth } from '../store/auth-context';
import type { CredentialListResponse } from '../types/identity';

/** The voice provider's credential slug in optr-identity. */
export const VOICE_CREDENTIAL_PROVIDER = 'xai';

export type VoiceAvailability = 'available' | 'unavailable' | 'unknown';

export function hasConnectedVoiceCredential(
  list: CredentialListResponse | undefined,
): boolean {
  return !!list?.providers?.some(
    (p) => p.id === VOICE_CREDENTIAL_PROVIDER && p.status === 'connected',
  );
}

interface TierView {
  /** undefined while the query is loading / disabled. */
  data: CredentialListResponse | undefined;
  /** True once the query has settled (success or error). */
  settled: boolean;
}

/** Pure resolution logic, extracted for unit tests. */
export function resolveVoiceAvailability(
  isAdmin: boolean,
  tiers: { user: TierView; space: TierView; org: TierView },
): VoiceAvailability {
  const views = [tiers.user, tiers.space, tiers.org];
  if (views.some((t) => hasConnectedVoiceCredential(t.data))) return 'available';
  // Only an admin sees the org tier, and only fully-settled queries prove a
  // negative — anything in flight could still surface the key.
  if (isAdmin && views.every((t) => t.settled)) return 'unavailable';
  return 'unknown';
}

export function useVoiceAvailability(spaceId: string | undefined): VoiceAvailability {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const my = useMyCredentials();
  const space = useSpaceCredentials(spaceId);
  const org = useOrgCredentials();

  return resolveVoiceAvailability(isAdmin, {
    user: { data: my.data, settled: my.isFetched },
    // A disabled space query (no spaceId) counts as settled — there is no
    // space tier to wait for.
    space: { data: space.data, settled: spaceId ? space.isFetched : true },
    org: { data: org.data, settled: org.isFetched },
  });
}
